// components/PaymentKPIBox.jsx
import React, { useState, useEffect, useRef } from 'react';
import { Card, Button, Toast } from 'primereact';
import axios from 'axios';
import useAuth from 'hooks/useAuth';

const API_URL = process.env.REACT_APP_API_PATH || 'http://localhost:5000/api';
const userId=useAuth.userId;
const PaymentKPIBox = () => {
const auth = useAuth(); // Get auth object
const [totalDue, setTotalDue] = useState(0);
const [loading, setLoading] = useState(false);
const toast = useRef(null);
// Get userId from auth
    const userId = auth?.user?.id || auth?.userId; // Try both possible structures

 useEffect(() => {
        if (userId) {
            loadTotalDue();
        }
    }, [userId]); // Re-run when userId changes


    const loadTotalDue = async () => {
        try {
            const response = await axios.get(`${API_URL}customer/user/${userId}/total-due`);
            if (response.data.status === 'success') {
                setTotalDue(response.data.totalDue);
            }
        } catch (error) {
            console.error('Error loading total due:', error);
        }
    };

    const loadRazorpayScript = () => {
        return new Promise((resolve) => {
            const script = document.createElement('script');
            script.src = 'https://checkout.razorpay.com/v1/checkout.js';
            script.onload = () => resolve(true);
            script.onerror = () => resolve(false);
            document.body.appendChild(script);
        });
    };

    const initializeRazorpay = async () => {
        const scriptLoaded = await loadRazorpayScript();
        
        if (!scriptLoaded) {
            showToast('error', 'Error', 'Failed to load Razorpay. Please check your internet connection.');
            return false;
        }
        
        return true;
    };

    const showToast = (severity, summary, detail) => {
        if (toast.current) {
            toast.current.show({
                severity: severity,
                summary: summary,
                detail: detail,
                life: 3000
            });
        }
    };

    const createOrder = async () => {
        try {
            setLoading(true);
            // Create order on your backend
            const response = await axios.post(`${API_URL}customer/create-order`, {
                amount: totalDue,
                userId: userId
            });

            if (response.data.status === 'success') {
                return response.data.order;
            } else {
                showToast('error', 'Error', response.data.message || 'Failed to create order');
                return null;
            }
        } catch (error) {
            console.error('Error creating order:', error);
            showToast('error', 'Error', 'Failed to create payment order');
            return null;
        } finally {
            setLoading(false);
        }
    };

 const handlePayment = async () => {
    console.log('Payment initiated. Total due:', totalDue);
    
    if (totalDue <= 0) {
        showToast('info', 'No Payment Due', 'You have no pending payments.');
        return;
    }

    const isRazorpayReady = await initializeRazorpay();
    console.log('Razorpay ready:', isRazorpayReady);
    
    if (!isRazorpayReady) return;

    const order = await createOrder();
    console.log('Order created:', order);
    
    if (!order) return;

    // Check if Razorpay key is configured
    const razorpayKey = process.env.REACT_APP_RAZORPAY_KEY_ID;
    console.log('Razorpay Key ID exists:', !!razorpayKey);
    
    if (!razorpayKey) {
        showToast('error', 'Configuration Error', 'Payment gateway not configured properly');
        return;
    }

    const options = {
        key: razorpayKey,
        amount: order.amount,
        currency: order.currency,
        name: "Laundry Service",
        description: `Payment for pending bookings - Total: ₹${totalDue.toFixed(2)}`,
        image: "/logo.png",
        order_id: order.id,
        handler: function (response) {
            console.log('Payment success response:', response);
            verifyPayment(response, order);
        },
        prefill: {
            name: order.user?.name || auth?.user?.name || '',
            email: order.user?.email || auth?.user?.email || '',
            contact: order.user?.phone || auth?.user?.phone || ''
        },
        notes: {
            userId: userId,
            totalDue: totalDue
        },
        theme: {
            color: "#F37254"
        },
        modal: {
            ondismiss: function() {
                console.log('Payment modal dismissed');
                showToast('info', 'Payment Cancelled', 'Payment was cancelled.');
            }
        }
    };

    try {
        console.log('Opening Razorpay with options:', options);
        const razorpay = new window.Razorpay(options);
        
        // Add event listeners for debugging
        razorpay.on('payment.failed', function(response) {
            console.error('Payment failed:', response.error);
            showToast('error', 'Payment Failed', response.error.description || 'Payment processing failed');
        });
        
        razorpay.open();
        console.log('Razorpay modal opened');
    } catch (error) {
        console.error('Error opening Razorpay:', error);
        showToast('error', 'Error', 'Failed to open payment gateway');
    }
};


    const verifyPayment = async (paymentResponse, order) => {
        try {
            setLoading(true);
            const verification = await axios.post(`${API_URL}customer/verify-payment`, {
                razorpay_payment_id: paymentResponse.razorpay_payment_id,
                razorpay_order_id: paymentResponse.razorpay_order_id,
                razorpay_signature: paymentResponse.razorpay_signature,
                order_id: order.id,
                userId: userId
            });

            if (verification.data.status === 'success') {
                showToast('success', 'Payment Successful', 'Your payment has been processed successfully!');
                // Refresh total due after successful payment
                await loadTotalDue();
                // Optional: Refresh other dashboard data
                if (window.onPaymentSuccess) {
                    window.onPaymentSuccess();
                }
            } else {
                showToast('error', 'Payment Failed', verification.data.message || 'Payment verification failed');
            }
        } catch (error) {
            console.error('Error verifying payment:', error);
            showToast('error', 'Payment Failed', 'Failed to verify payment. Please contact support.');
        } finally {
            setLoading(false);
        }
    };

    return (
        <>
            <Toast ref={toast} position="top-center" />
            
            <Card className="mb-4 shadow-3 border-round-xl" style={{ 
                background: totalDue > 0 
                    ? 'linear-gradient(135deg, var(--orange-500) 0%, var(--orange-700) 100%)'
                    : 'linear-gradient(135deg, var(--green-500) 0%, var(--green-700) 100%)',
                cursor: totalDue > 0 ? 'pointer' : 'default',
                transition: 'all 0.3s ease'
            }}>
                <div className="flex align-items-center justify-content-between p-3">
                    <div className="flex align-items-center">
                        <div className="flex align-items-center justify-content-center border-circle mr-3" 
                            style={{ 
                                backgroundColor: 'rgba(255,255,255,0.2)', 
                                width: '56px', 
                                height: '56px' 
                            }}>
                            <i className="pi pi-credit-card text-white" style={{ fontSize: '2rem' }}></i>
                        </div>
                        <div>
                            <div className="text-white opacity-90 text-sm font-medium mb-1">
                                {totalDue > 0 ? 'Total Pending Payment' : 'All Payments Settled'}
                            </div>
                            <div className="text-white text-3xl font-bold">
                                ₹{totalDue.toFixed(2)}
                            </div>
                            {totalDue > 0 && (
                                <div className="text-white text-xs opacity-80 mt-1">
                                    Click to pay now
                                </div>
                            )}
                        </div>
                    </div>
                    
                    {totalDue > 0 && (
                        <Button 
                            label={loading ? "Processing..." : "Pay Now"} 
                            icon={loading ? "pi pi-spinner pi-spin" : "pi pi-arrow-right"}
                            className="p-button-lg p-button-outlined text-white border-white"
                            style={{ 
                                borderColor: 'white', 
                                color: 'white',
                                minWidth: '120px'
                            }}
                            onClick={handlePayment}
                            disabled={loading}
                        />
                    )}
                    
                    {totalDue === 0 && (
                        <div className="flex align-items-center">
                            <i className="pi pi-check-circle text-white" style={{ fontSize: '1.5rem' }}></i>
                            <span className="text-white ml-2 font-medium">No Dues</span>
                        </div>
                    )}
                </div>
            </Card>
        </>
    );
};

export default PaymentKPIBox;
